import api from "./axiosConfig";

export interface SingleSendStatsViewModel {
  id: string;
  name: string;
  status: string | null;
  sendAt: string | null;
  requests: number;
  delivered: number;
  opens: number;
  uniqueOpens: number;
  clicks: number;
  uniqueClicks: number;
  unsubscribes: number;
  bounces: number;
  spamReports: number;
  openRate: number;
  ctr: number;
  ctor: number;
  bounceRate: number;
  unsubscribeRate: number;
}

export interface NewsletterCommunicationStatisticViewModel {
  singleSends: SingleSendStatsViewModel[];
}

export interface NewsletterInsightsRequest {
  from?: string; // ISO 8601, e.g. "2026-01-01T00:00:00+00:00"
  to?: string;   // ISO 8601, e.g. "2026-03-25T23:59:59+00:00"
}

export interface PushNotificationStatsViewModel {
  iosOpens: number;
  androidOpens: number;
  webOpens: number;
  totalOpens: number;
  iosForeground: number;
  androidForeground: number;
  webForeground: number;
  totalForeground: number;
  iosDismissals: number;
  androidDismissals: number;
  webDismissals: number;
  totalDismissals: number;
}

export interface PushNotificationStatsRequest {
  from?: string;
  to?: string;
}

export const getPushNotificationStatsForAdmin = async (
  params: PushNotificationStatsRequest = {}
): Promise<PushNotificationStatsViewModel> => {
  try {
    const response = await api.post<PushNotificationStatsViewModel>(
      "/notification/get-push-notification-stats-for-admin",
      params
    );
    return response.data;
  } catch (error) {
    console.error("Getting push notification stats error:", error);
    throw error;
  }
};

/**
 * One metric split by platform. Null means "not measured" — an iOS receipt before the app could
 * report one, a web slice before the site instrumented it — as opposed to a measured zero.
 */
export interface PlatformSplit {
  ios: number | null;
  android: number | null;
  web: number | null;
  /** Sum of measured platforms; for pre-split campaigns the only figure, with components null. */
  total: number | null;
}

export interface PlatformRate {
  /** Percent, e.g. 10.89. */
  value: number;
  /** The denominator was the calibrated estimate — rendered with ≈, never as exact. */
  isApproximate: boolean;
}

export interface OpenRateSplit {
  ios: PlatformRate | null;
  android: PlatformRate | null;
  web: PlatformRate | null;
  total: PlatformRate | null;
}

export interface PushCampaignStatRow {
  campaignName: string;
  analyticsLabel: string | null;
  /** The day this send went out — what Firebase shows as the campaign's Start. */
  sendDate: string;
  /** Last day this send produced any event, including stragglers. Not the send date. */
  lastStatDate: string | null;
  linkedBusinessId: string | null;
  linkedBusinessName: string | null;
  sentAt: string | null;
  impressions: number;
  opens: number;
  uniqueOpens: number;
  dismisses: number;
  foreground: number;
  openRate: number;
  sends: number;
  delivered: number;
  consoleSendsRange: string | null;
  /**
   * The console's Sends bucket estimated from our registered devices, for recent broadcasts with no
   * exact figure yet. Approximate and all-platform; superseded by the real number when it arrives.
   */
  estimatedSendsRange: string | null;
  /** The console's Sends reproduced from the FCM export, bucketed — total-level, exact. */
  acceptedSendsRange: string | null;
  /** Messages FCM accepted, exact per platform — all null until the send's bulks were identified. */
  sent: PlatformSplit;
  /** The calibrated device-count estimate frozen when the send was first seen. */
  sentEstimated: PlatformSplit;
  /** Notifications that reached a device: delivery export first, else Android's receipts. */
  received: PlatformSplit;
  /** Opens plus foreground arrivals. */
  opened: PlatformSplit;
  /** The foreground share inside opened. */
  openedForeground: PlatformSplit;
  /** Explicit dismissals; iOS stays null while its events arrive without a campaign name. */
  dismissed: PlatformSplit;
  /**
   * Dismissals placed only by the day they happened — iOS sends them nameless, so a send that
   * owned its day alone is offered the day's, transactional pushes included. Shown with "≈".
   */
  dismissedApproximate: PlatformSplit;
  openRates: OpenRateSplit;
}

export interface PushCampaignStatsViewModel {
  campaigns: PushCampaignStatRow[];
}

export interface PushCampaignStatsRequest extends PushNotificationStatsRequest {
  /**
   * IOS | ANDROID | WEB — restricts the measured metrics to one platform. Per-platform rows exist
   * since late July 2026; older campaigns have a single ALL row and disappear under this filter.
   */
  platform?: string;
}

export const getFirebasePushCampaignStatsForAdmin = async (
  params: PushCampaignStatsRequest = {}
): Promise<PushCampaignStatRow[]> => {
  try {
    const response = await api.post<PushCampaignStatsViewModel>(
      "/notification/get-firebase-push-campaign-stats-for-admin",
      params
    );
    return response.data.campaigns ?? [];
  } catch (error) {
    console.error("Getting firebase push campaign stats error:", error);
    throw error;
  }
};

export interface PushCampaignPartnerImpactRow {
  campaignName: string;
  businessContentfulId: string;
  businessName: string | null;
  /** With the name, identifies the send — a campaign name can cover several. */
  sendDate: string;
  windowStart: string;
  windowEnd: string;
  /** False while the 96 hours are still running, so partial numbers are not read as final. */
  windowComplete: boolean;
  /** False when no sale of this partner ever reaches us — its zeros are blindness, not failure. */
  salesTracked: boolean;
  salesCount: number;
  salesAmount: number;
  assignedCashback: number;
}

export interface PushCampaignPartnerImpactViewModel {
  campaigns: PushCampaignPartnerImpactRow[];
}

/**
 * Fetched separately from the campaign list: this one reads the transaction tables, and the tab
 * renders the list without waiting for it.
 *
 * Only campaigns whose push carried a deeplink appear here — the promoted partner is read off that
 * deeplink by the nightly sync. Campaigns promoting a game, a section or generic content are absent
 * and render as "n/a".
 */
export const getPushCampaignPartnerImpactForAdmin = async (
  params: PushNotificationStatsRequest = {}
): Promise<PushCampaignPartnerImpactRow[]> => {
  try {
    const response = await api.post<PushCampaignPartnerImpactViewModel>(
      "/notification/get-push-campaign-partner-impact-for-admin",
      params
    );
    return response.data.campaigns ?? [];
  } catch (error) {
    console.error("Getting push campaign partner impact error:", error);
    throw error;
  }
};

export const getNewsletterInsightsForAdmin = async (
  params: NewsletterInsightsRequest = {}
): Promise<SingleSendStatsViewModel[]> => {
    try {
      const response =
        await api.post<NewsletterCommunicationStatisticViewModel>(
          "/business-account/get-newsletter-communication-statistic-for-admin",
          params
        );
      return response.data.singleSends ?? [];
    } catch (error) {
      console.error("Getting newsletter insights error:", error);
      throw error;
    }
  };
